import React, { createContext, useEffect, useState } from "react";
import axiosConfig from "../util/axiosConfig";
import { clearStoredAuth, getStoredToken, setStoredToken } from "../util/authStorage";
import {
  attachPushTokenRefreshOnFocus,
  syncPushTokenIfGranted,
  unregisterPushTokenBeforeLogout
} from "../util/pushNotifications";

const UserContext = createContext();

function UserContextProvider(props) {
  const [user, setUser] = useState(undefined);
  const [isLoading, setIsLoading] = useState(true);

  async function getUser() {
    try {
      const userRes = await axiosConfig.get(`/auth/loggedIn`);
      if (userRes.data) {
        setUser(userRes.data);
        localStorage.setItem('user', JSON.stringify(userRes.data));
        attachPushTokenRefreshOnFocus();
        syncPushTokenIfGranted({ preferServer: true }).catch((err) => {
          console.warn('Push token sync after auth check failed:', err);
        });
      } else {
        setUser(null);
        clearStoredAuth();
      }
    } catch (error) {
      console.error('Get user error:', error);
      setUser(null);
      clearStoredAuth();
    } finally {
      setIsLoading(false);
    }
  }

  async function logoutUser() {
    try {
      setIsLoading(true);
      await unregisterPushTokenBeforeLogout();
      await axiosConfig.get(`/auth/logOut`);
      setUser(null);
      clearStoredAuth();
    } catch (error) {
      console.error('Logout error:', error);
      setUser(null);
      clearStoredAuth();
    } finally {
      setIsLoading(false);
    }
  }

  useEffect(() => {
    const initializeAuth = async () => {
      try {
        const params = new URLSearchParams(window.location.search);
        const urlToken = params.get('token');
        const urlUser = params.get('user');

        if (urlToken) {
          setStoredToken(urlToken);
        }

        if (urlUser) {
          try {
            const decoded = decodeURIComponent(urlUser);
            const parsedUser = JSON.parse(decoded);
            localStorage.setItem('user', JSON.stringify(parsedUser));
          } catch (e) {
            try {
              const parsedUser = JSON.parse(urlUser);
              localStorage.setItem('user', JSON.stringify(parsedUser));
            } catch (e2) {
              // Ignore malformed user payload
            }
          }
        }

        if (urlToken || urlUser) {
          const cleanUrl = window.location.origin + window.location.pathname;
          window.history.replaceState({}, document.title, cleanUrl);
        }
      } catch (e) {
        // swallow URL parsing errors
      }

      const token = getStoredToken();
      const storedUser = localStorage.getItem('user');

      if (token && storedUser) {
        setUser(JSON.parse(storedUser));
        attachPushTokenRefreshOnFocus();
        syncPushTokenIfGranted({ preferServer: true }).catch((err) => {
          console.warn('Push token sync on session restore failed:', err);
        });
        setIsLoading(false);
        getUser();
      } else {
        setUser(null);
        setIsLoading(false);
      }
    };

    initializeAuth();
  }, []);

  return (
    <UserContext.Provider value={{
      user,
      getUser,
      logoutUser,
      isLoading,
      setUser,
    }}>
      {props.children}
    </UserContext.Provider>
  );
}

export default UserContext;
export { UserContextProvider };
